// PortalAdapter — Default implementation for React portals (tooltips, context menus)
// Injected into VNInvestCharts to avoid direct document.body access
// All global access is lazy-initialized inside the factory functions for tree-shaking
// ============================================
// Default Portal Adapter (uses document.body)
// ============================================
export const createDefaultPortalAdapter = (containerSelector) => {
    let cachedContainer = null;
    const getContainer = () => {
        if (cachedContainer)
            return cachedContainer;
        if (containerSelector && typeof document !== 'undefined') {
            const element = document.querySelector(containerSelector);
            if (element) {
                cachedContainer = element;
                return element;
            }
        }
        // Fallback to document.body
        if (typeof document !== 'undefined' && document.body) {
            cachedContainer = document.body;
            return document.body;
        }
        // SSR fallback: create a detached div (will not render visually)
        if (typeof document !== 'undefined') {
            const fallback = document.createElement('div');
            fallback.style.display = 'none';
            cachedContainer = fallback;
            return fallback;
        }
        // In SSR without document, return a minimal object
        const fallback = { style: { display: 'none' } };
        cachedContainer = fallback;
        return fallback;
    };
    return { getContainer };
};
// Singleton - lazy initialized on first access
let _defaultPortalAdapter = null;
export const getDefaultPortalAdapter = () => {
    if (!_defaultPortalAdapter) {
        _defaultPortalAdapter = createDefaultPortalAdapter();
    }
    return _defaultPortalAdapter;
};
// Backward compatibility - lazy getter
export const defaultPortalAdapter = new Proxy({}, {
    get(target, prop) {
        const adapter = getDefaultPortalAdapter();
        return adapter[prop];
    }
});
// ============================================
// Custom Container Adapter (for Shadow DOM / Micro-frontends)
// ============================================
export const createCustomPortalAdapter = (container) => {
    return {
        getContainer: () => container,
    };
};
export const createMultiPortalAdapter = (containers) => {
    // Lazy default container resolution
    const getDefaultContainer = () => {
        if (typeof document !== 'undefined' && document.body) {
            return document.body;
        }
        if (typeof document !== 'undefined') {
            const fallback = document.createElement('div');
            fallback.style.display = 'none';
            return fallback;
        }
        return { style: { display: 'none' } };
    };
    return {
        getTooltipContainer: () => containers?.getTooltipContainer?.() ?? getDefaultContainer(),
        getContextMenuContainer: () => containers?.getContextMenuContainer?.() ?? getDefaultContainer(),
        getModalContainer: () => containers?.getModalContainer?.() ?? getDefaultContainer(),
        getNotificationContainer: () => containers?.getNotificationContainer?.() ?? getDefaultContainer(),
    };
};
// ============================================
// Portal Adapter with Auto-Creation (for isolated environments)
// ============================================
export const createAutoPortalAdapter = (rootId = 'vninvestcharts-portals') => {
    let container = null;
    const getContainer = () => {
        if (container)
            return container;
        if (typeof document === 'undefined') {
            // SSR: return detached element
            return { style: { display: 'none' } };
        }
        // Try to find existing
        let existing = document.getElementById(rootId);
        if (existing) {
            container = existing;
            return container;
        }
        // Create new portal root
        container = document.createElement('div');
        container.id = rootId;
        container.style.position = 'fixed';
        container.style.top = '0';
        container.style.left = '0';
        container.style.width = '100%';
        container.style.height = '100%';
        container.style.pointerEvents = 'none'; // Let portals handle their own pointer events
        container.style.zIndex = '9999';
        if (document.body) {
            document.body.appendChild(container);
        }
        return container;
    };
    return { getContainer };
};
export const createMockPortalAdapter = () => {
    let mockContainer = null;
    const adapter = {
        getContainer: () => {
            adapter.getContainerCalls++;
            if (mockContainer)
                return mockContainer;
            // Return a minimal mock container (no document access)
            return { style: {} };
        },
        getContainerCalls: 0,
        resetCalls: () => { adapter.getContainerCalls = 0; },
        setMockContainer: (c) => { mockContainer = c; },
    };
    return adapter;
};
